/**
 * useLiveRingState hook.
 *
 * Subscribes to WebSocket detection + alert events for a site and merges them
 * into ring activity state consumed by ConcentricZonesViz:
 *
 *   deviceCountPerRing  - number of recently-seen devices per zone tier
 *   activeAlertRings    - animated alert ring states for alert lifecycle viz
 *
 * Detection decay: device counts decay 120s after last detection timestamp.
 * Alert ring lifecycle:
 *   1. approach -> state='collapsing' (ring collapses inward to tier radius)
 *   2. turnaround within 120s -> state='turning-away'
 *   3. entry within 120s -> state='entered'
 *
 * Zone resolution: `zoneIdToRing` maps integer zone_id from the wire to the
 * ZoneId string ('perimeter' | 'threshold' | 'interior' | 'core') by looking
 * up the zone's position in liveState.zones. Uses index-based fallback since
 * plan 03-05 zones use string id field, not integer.
 *
 * Plan 03-06 implementation (CL-04, API-09).
 */
import { useState, useEffect, useMemo } from 'react';
import { useWebSocket } from '@/features/realtime/useWebSocket';
import type { ZoneId, LiveState } from './schemas';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Tier index for ring geometry (1 = perimeter/outer, 4 = core/center). */
export type RingTier = 1 | 2 | 3 | 4;

/** Animation state for a single active alert ring. */
export interface AlertRingEntry {
  /** Alert primary key from fleet-api. */
  alertId: number;
  /** Concentric ring tier (maps to ring radius). */
  tier: RingTier;
  /** Device fingerprint (used to match followup resolution events). */
  fingerprint: string | null;
  /** Current animation phase. */
  state: 'collapsing' | 'settled' | 'turning-away' | 'entered';
  /** Epoch ms when the alert was opened (determines 120s resolution window). */
  openedAt: number;
}

/** Internal detection record with parsed timestamp. */
interface DetectionEntry {
  fingerprint: string | null;
  rssi: number;
  zoneId: number | null;
  ts: number; // epoch ms
}

/** Full ring state returned by the hook. */
export interface RingState {
  /** Per-tier device counts derived from recent detections. */
  deviceCountPerRing: Record<ZoneId, number>;
  /** Currently animated alert rings. */
  activeAlertRings: AlertRingEntry[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Detection decay window in milliseconds (2 minutes). */
const DETECTION_DECAY_MS = 120_000;

/** Alert ring matching window in milliseconds (2 minutes). */
const ALERT_MATCH_WINDOW_MS = 120_000;

/** Decay loop interval in milliseconds. */
const DECAY_INTERVAL_MS = 2_000;

// ---------------------------------------------------------------------------
// Zone ID resolution
// ---------------------------------------------------------------------------

/** Ordered ZoneId strings matching tier numbers 1-4. */
const TIER_TO_ZONE_ID: Record<1 | 2 | 3 | 4, ZoneId> = {
  1: 'perimeter',
  2: 'threshold',
  3: 'interior',
  4: 'core',
};

/**
 * Resolves a numeric zone_id from the wire to a ZoneId string tier.
 *
 * Strategy:
 *   1. If zone_id is null/undefined or liveState is missing, return 'perimeter' silently.
 *   2. Look up liveState.zones by index (zones are ordered by tier 1..4).
 *      If found, return zone.id (which is the ZoneId string in plan 03-05 schema).
 *   3. If not found, warn and return 'perimeter'.
 *
 * Note: plan 03-05 uses string 'id' field on zones rather than integer PK,
 * so we map by tier field. The wire sends integer zone_id from fleet-api.
 * We resolve by finding the zone whose tier matches the numeric ID's position.
 *
 * @param zoneId - Integer zone_id from wire (nullable)
 * @param liveState - Current live-state zones snapshot (nullable)
 * @returns ZoneId string tier name
 */
export function zoneIdToRing(zoneId: number | null, liveState: LiveState | undefined): ZoneId {
  if (zoneId === null || zoneId === undefined || !liveState) {
    return 'perimeter';
  }

  // Try to find a zone whose tier matches the numeric zone_id
  // (fleet-api zone PKs are sequential 1..4 matching tier numbers in practice)
  const tier = zoneId as 1 | 2 | 3 | 4;
  if (tier >= 1 && tier <= 4 && TIER_TO_ZONE_ID[tier]) {
    return TIER_TO_ZONE_ID[tier];
  }

  // Fallback: try to find by liveState index
  if (zoneId >= 1 && zoneId <= liveState.zones.length) {
    const zone = liveState.zones[zoneId - 1];
    if (zone) return zone.id;
  }

  console.warn(`[useLiveRingState] unknown zone_id=${zoneId}; defaulting to perimeter`);
  return 'perimeter';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useLiveRingState: merges live WebSocket events into ring activity state.
 *
 * @param siteId - Site UUID; subscribes to site-specific detection channel
 * @param liveState - Current live state from TanStack Query (for zone resolution)
 * @returns RingState containing deviceCountPerRing + activeAlertRings
 */
export function useLiveRingState(
  siteId: string,
  liveState: LiveState | undefined,
): RingState {
  const { subscribe, unsubscribe, onMessage } = useWebSocket();

  const [alertRings, setAlertRings] = useState<AlertRingEntry[]>([]);
  const [detections, setDetections] = useState<DetectionEntry[]>([]);

  // ---------------------------------------------------------------------------
  // Subscriptions + message dispatch
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!siteId) return;

    subscribe('alerts');
    subscribe(`site:${siteId}:detections`);

    const unsubMsg = onMessage((frame) => {
      if (frame.type === 'detection') {
        const p = frame.payload;
        setDetections((prev) => [
          ...prev,
          {
            fingerprint: p.fingerprint,
            rssi: p.rssi,
            zoneId: p.zone_id,
            ts: new Date(p.ts).getTime(),
          },
        ]);
      }

      if (frame.type === 'alert') {
        const p = frame.payload;
        const alertType = p.alert_type; // canonical from plan 03-02

        if (alertType === 'approach' || alertType === 'entry') {
          setAlertRings((prev) => {
            // Check if there is an existing collapsing ring for this fingerprint within the window
            const now = Date.now();
            if (p.fingerprint) {
              const existing = prev.find(
                (r) =>
                  r.fingerprint === p.fingerprint &&
                  r.state === 'collapsing' &&
                  now - r.openedAt < ALERT_MATCH_WINDOW_MS,
              );
              if (existing && alertType === 'entry') {
                // Immediate entry (no prior approach) or reclassification
                return prev.map((r) =>
                  r.alertId === existing.alertId ? { ...r, state: 'entered' } : r,
                );
              }
            }

            // New alert ring
            const tier = (p.zone_id !== null && p.zone_id !== undefined && p.zone_id >= 1 && p.zone_id <= 4
              ? p.zone_id
              : 1) as RingTier;

            const newRing: AlertRingEntry = {
              alertId: p.alert_id,
              tier,
              fingerprint: p.fingerprint,
              state: alertType === 'entry' ? 'entered' : 'collapsing',
              openedAt: now,
            };

            // Cap total rings at 5 stacked (design spec); beyond 5 aggregate into thicker ring
            const MAX_RINGS = 5;
            const existing = prev.length >= MAX_RINGS ? prev.slice(1) : prev;
            return [...existing, newRing];
          });
        }

        if (alertType === 'turnaround' || alertType === 'loiter_resolved') {
          setAlertRings((prev) => {
            const now = Date.now();
            return prev.map((r) => {
              if (
                p.fingerprint &&
                r.fingerprint === p.fingerprint &&
                r.state === 'collapsing' &&
                now - r.openedAt < ALERT_MATCH_WINDOW_MS
              ) {
                return { ...r, state: 'turning-away' };
              }
              return r;
            });
          });
        }
      }
    });

    return () => {
      unsubscribe('alerts');
      unsubscribe(`site:${siteId}:detections`);
      unsubMsg();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  // ---------------------------------------------------------------------------
  // Decay loop: drop old detections + resolved rings
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();

      setDetections((prev) =>
        prev.filter((d) => {
          const age = now - d.ts;
          return age < DETECTION_DECAY_MS;
        }),
      );

      setAlertRings((prev) =>
        prev.filter((r) => {
          // Remove turning-away rings after animation completes (800ms lifespan)
          if (r.state === 'turning-away') {
            return now - r.openedAt < 1500; // 600ms collapse + 800ms expand + buffer
          }
          return true;
        }),
      );
    }, DECAY_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  // ---------------------------------------------------------------------------
  // Derived counts
  // ---------------------------------------------------------------------------

  const deviceCountPerRing = useMemo<Record<ZoneId, number>>(() => {
    const counts: Record<ZoneId, number> = {
      perimeter: 0,
      threshold: 0,
      interior: 0,
      core: 0,
    };

    const now = Date.now();
    for (const d of detections) {
      if (now - d.ts >= DETECTION_DECAY_MS) continue; // skip stale (may not be cleaned yet)
      const zone = zoneIdToRing(d.zoneId, liveState);
      counts[zone] = (counts[zone] ?? 0) + 1;
    }

    return counts;
  }, [detections, liveState]);

  return {
    deviceCountPerRing,
    activeAlertRings: alertRings,
  };
}
